// src/pages/inventory-control-center/components/AddItemModal.jsx
import React, { useCallback } from "react";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";
import Image from "../../../components/appImage";
import { FileUpload } from "../../../components/FileUpload";
import ProductFiles from "../../../components/ProductFiles";
import { useAddItemForm } from "../hooks/useAddItemForm";

const AddItemModal = ({
  isOpen,
  onClose,
  onSave,
  userRole = "admin",
  availableFilters, // por si luego quieres usarlo
  showToast,
}) => {
  const {
    formData,
    activeStep,
    imagePreview,
    isSubmitting,
    newProductId,
    saveError,
    saveSuccess,
    fileInputRef,
    categoryOptions,
    supplierOptions,
    locationOptions,
    unitOptions,
    steps,
    handleInputChange,
    handleNumberChange,
    handleImageUpload,
    handleRemoveImage,
    handleFileUploaded,
    handleSubmit,
    handleClose,
    nextStep,
    prevStep,
    generateBarcode,
    generateSKU,
    calculateMargin,
    isStepValid,
  } = useAddItemForm({
    isOpen,
    onSave,
    onClose,
    userRole,
    showToast,
  });

  // Animaciones
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.98,
      y: 10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.18,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      y: 10,
      transition: {
        duration: 0.14,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // StockStatusIndicator interno
  const StockStatusIndicator = useCallback(() => {
    const getStockStatus = () => {
      const currentStock = parseInt(formData.currentStock) || 0;
      const minimumStock = parseInt(formData.minimumStock) || 0;

      if (currentStock === 0)
        return {
          status: "out-of-stock",
          color: "red",
          label: "Sin Stock",
        };
      if (currentStock <= minimumStock)
        return {
          status: "low-stock",
          color: "amber",
          label: "Stock Bajo",
        };
      if (currentStock > minimumStock * 2)
        return {
          status: "overstock",
          color: "cyan",
          label: "Stock Alto",
        };
      return { status: "adequate", color: "blue", label: "Stock Adecuado" };
    };

    const status = getStockStatus();

    const colorClasses = {
      red: "bg-red-500/10 border-red-500/25 text-red-400",
      amber: "bg-amber-500/10 border-amber-500/25 text-amber-400",
      cyan: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
      blue: "bg-blue-500/10 border-blue-500/25 text-blue-300",
    };

    return (
      <div
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-xs sm:text-sm ${colorClasses[status.color]
          }`}
      >
        <div
          className={`w-2 h-2 rounded-full ${status.color === "red"
              ? "bg-red-500"
              : status.color === "amber"
                ? "bg-amber-500"
                : status.color === "cyan"
                  ? "bg-cyan-400"
                  : "bg-blue-400"
            }`}
        ></div>
        <span className="font-medium">{status.label}</span>
      </div>
    );
  }, [formData.currentStock, formData.minimumStock]);

  const StepIndicator = useCallback(({ step, isActive, isCompleted }) => {
    return (
      <div
        className={`
          relative flex items-center justify-center 
          w-8 h-8 sm:w-10 sm:h-10 
          rounded-xl border-2 
          transition-all duration-300
          bg-gray-900 
          border-cyan-700/60 
          shadow-[0_0_12px_rgba(34,211,238,0.28)]
        `}
      >
        {isCompleted ? (
          <span className="text-cyan-200 font-bold text-sm">✓</span>
        ) : (
          <span
            className={`font-semibold text-sm ${isActive ? "text-cyan-100" : "text-slate-400"
              }`}
          >
            {step.number}
          </span>
        )}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-900 px-1 sm:px-2 py-0.5 sm:py-1 rounded border border-cyan-900/70">
          <span className="text-[10px] sm:text-xs font-semibold text-slate-300">
            {step.number}
          </span>
        </div>
      </div>
    );
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 p-0 safe-area-inset overflow-hidden">
      {/* Backdrop */}
      <motion.div
        key="modal-backdrop"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        transition={{ duration: 0.18 }}
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal Principal FULL SCREEN */}
      <motion.div
        key="modal-content"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="
          relative z-10 w-full h-full 
          bg-gray-900/95 
          border border-cyan-900/60
          shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_30px_100px_rgba(0,0,0,0.9)] 
          rounded-none 
          flex flex-col overflow-hidden
        "
      >
        {/* Header */}
        <div
          className="
          relative 
          bg-gray-900 
          border-b border-cyan-900/60 
          px-4 sm:px-8 py-4 sm:py-6 
          flex items-start justify-between gap-3
        "
        >
          <div className="flex items-start space-x-3 sm:space-x-4">
            <div
              className="
              p-2 sm:p-3 
              rounded-2xl 
              bg-gray-800 
              border border-cyan-600/70 
              flex items-center justify-center
            "
            >
              <span className="text-cyan-300 text-lg">+</span>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-3xl font-semibold text-slate-50 truncate">
                {saveSuccess ? "✅ Producto guardado" : "Nuevo producto"}
              </h2>
              <p className="text-slate-400 mt-1 text-sm sm:text-base">
                {saveSuccess
                  ? "El producto se ha guardado exitosamente."
                  : "Completa la información del producto paso a paso."}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isSubmitting}
            className="hover:bg-gray-800 rounded-xl disabled:opacity-50"
          >
            <span className="text-xl text-slate-300">×</span>
          </Button>
        </div>

        {/* Mensajes de Error */}
        {saveError && (
          <div className="mx-4 sm:mx-8 mt-3 p-3 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-red-400">⚠</span>
              <span className="text-red-400 text-sm">{saveError}</span>
            </div>
          </div>
        )}

        {/* Mensajes de Éxito */}
        {saveSuccess && (
          <div className="mx-4 sm:mx-8 mt-3 p-3 sm:p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-cyan-300">✓</span>
              <span className="text-cyan-200 text-sm">
                Producto guardado exitosamente. Cerrando automáticamente...
              </span>
            </div>
          </div>
        )}

        {/* Steps */}
        {!saveSuccess && (
          <div
            className="
            px-4 sm:px-8 py-3 sm:py-4 
            border-b border-cyan-900/50 
            bg-gray-900/95
          "
          >
            <div className="flex items-center justify-between overflow-x-auto">
              {steps.map((step, index) => (
                <div
                  key={step.number}
                  className="flex items-center flex-1 min-w-0"
                >
                  <div className="flex items-center">
                    <StepIndicator
                      step={step}
                      isActive={activeStep === step.number}
                      isCompleted={activeStep > step.number}
                    />
                    <div className="ml-2 sm:ml-4 hidden sm:block">
                      <div
                        className={`text-sm font-semibold ${activeStep >= step.number
                            ? "text-slate-50"
                            : "text-slate-400"
                          }`}
                      >
                        {step.title}
                      </div>
                      <div className="text-xs text-slate-500">
                        {step.description}
                      </div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 sm:mx-6 ${activeStep > step.number
                          ? "bg-cyan-500"
                          : "bg-gray-700"
                        }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contenido */}
        {!saveSuccess && (
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <div className="p-4 sm:p-8 space-y-6">
                {/* Paso 1 */}
                {activeStep === 1 && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      {/* Izquierda */}
                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5
                        "
                        >
                          <label className="block text-sm font-semibold text-slate-50 mb-2 sm:mb-3">
                            Identificación del producto
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="space-y-2">
                              <Input
                                label="Código/SKU *"
                                value={formData.code}
                                onChange={(e) =>
                                  handleInputChange("code", e.target.value)
                                }
                                placeholder="SENS-1234"
                                className="bg-gray-950/70 border-gray-700 text-slate-50"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={generateSKU}
                                className="w-full border-cyan-800 text-cyan-200 hover:bg-gray-900"
                              >
                                Generar SKU
                              </Button>
                            </div>

                            <div className="space-y-2">
                              <Input
                                label="Código de barras"
                                value={formData.barcode}
                                onChange={(e) =>
                                  handleInputChange("barcode", e.target.value)
                                }
                                placeholder="13 dígitos"
                                className="bg-gray-950/70 border-gray-700 text-slate-50"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={generateBarcode}
                                className="w-full border-cyan-800 text-cyan-200 hover:bg-gray-900"
                              >
                                Generar código
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5 space-y-4
                        "
                        >
                          <Input
                            label="Nombre del producto *"
                            value={formData.name}
                            onChange={(e) =>
                              handleInputChange("name", e.target.value)
                            }
                            placeholder="Nombre descriptivo y comercial del producto"
                            className="bg-gray-950/70 border-gray-700 text-slate-50"
                          />

                          <Input
                            label="Descripción"
                            type="textarea"
                            rows={3}
                            value={formData.description}
                            onChange={(e) =>
                              handleInputChange("description", e.target.value)
                            }
                            placeholder="Descripción detallada, características técnicas, especificaciones..."
                            className="bg-gray-950/70 border-gray-700 text-slate-50"
                          />
                        </div>
                      </div>

                      {/* Derecha */}
                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5 space-y-4
                        "
                        >
                          <Select
                            label="Categoría *"
                            options={categoryOptions}
                            value={formData.category}
                            onChange={(value) =>
                              handleInputChange("category", value)
                            }
                            placeholder="Seleccione una categoría"
                          />

                          <Select
                            label="Unidad de medida"
                            options={unitOptions}
                            value={formData.unit}
                            onChange={(value) =>
                              handleInputChange("unit", value)
                            }
                          />

                          <div className="bg-gray-950/80 rounded-xl border border-cyan-900/60 p-3 sm:p-4">
                            <h4 className="text-sm font-semibold text-slate-50 mb-2 sm:mb-3">
                              Vista previa
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-400">SKU:</span>
                                <span className="text-slate-100 font-mono">
                                  {formData.code || "---"}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">Nombre:</span>
                                <span className="text-slate-100 truncate">
                                  {formData.name || "---"}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-400">
                                  Categoría:
                                </span>
                                <span className="text-cyan-300">
                                  {formData.category
                                    ? categoryOptions
                                      .find(
                                        (c) => c.value === formData.category
                                      )
                                      ?.label.split(" ")
                                      .slice(1)
                                      .join(" ")
                                    : "---"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paso 2: Stock y precios */}
                {activeStep === 2 && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                      {/* Precios */}
                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5
                        "
                        >
                          <label className="block text-sm font-semibold text-slate-50 mb-3 sm:mb-4">
                            Información de precios
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <Input
                              label="Costo de adquisición *"
                              type="number"
                              step="0.01"
                              value={formData.cost}
                              onChange={(e) =>
                                handleNumberChange("cost", e.target.value)
                              }
                              placeholder="0.00"
                              prefix="MXN"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />

                            <Input
                              label="Precio de venta *"
                              type="number"
                              step="0.01"
                              value={formData.price}
                              onChange={(e) =>
                                handleNumberChange("price", e.target.value)
                              }
                              placeholder="0.00"
                              prefix="MXN"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />
                          </div>

                          <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-950/80 rounded-xl border border-cyan-900/60">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                              <div>
                                <div className="text-xs text-cyan-300 font-semibold uppercase tracking-wide">
                                  Margen de ganancia
                                </div>
                                <div className="text-2xl sm:text-3xl font-bold text-slate-50">
                                  {calculateMargin().toFixed(1)}%
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-slate-400">
                                  Ganancia por unidad
                                </div>
                                <div className="text-lg font-semibold text-cyan-300">
                                  MXN{" "}
                                  {formData.cost && formData.price
                                    ? (formData.price - formData.cost).toFixed(
                                      2
                                    )
                                    : "0.00"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Stock */}
                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5 space-y-4
                        "
                        >
                          <label className="block text-sm font-semibold text-slate-50 mb-2 sm:mb-1">
                            Gestión de stock
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            <Input
                              label="Stock inicial"
                              type="number"
                              value={formData.currentStock}
                              onChange={(e) =>
                                handleNumberChange(
                                  "currentStock",
                                  e.target.value
                                )
                              }
                              placeholder="0"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />

                            <Input
                              label="Stock mínimo"
                              type="number"
                              value={formData.minimumStock}
                              onChange={(e) =>
                                handleNumberChange(
                                  "minimumStock",
                                  e.target.value
                                )
                              }
                              placeholder="0"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />

                            <Input
                              label="Stock máximo"
                              type="number"
                              value={formData.maximumStock}
                              onChange={(e) =>
                                handleNumberChange(
                                  "maximumStock",
                                  e.target.value
                                )
                              }
                              placeholder="Opcional"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />
                          </div>

                          <div className="mt-3 sm:mt-4">
                            <StockStatusIndicator />
                          </div>
                        </div>

                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 sm:p-4">
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <span className="text-amber-300">⚠</span>
                            <div>
                              <div className="text-sm font-semibold text-amber-200">
                                Sistema de alertas
                              </div>
                              <div className="text-xs text-amber-200/80 mt-1">
                                Notificaciones cuando el stock alcance el nivel
                                mínimo configurado.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paso 3: Ubicación y Control */}
                {activeStep === 3 && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5 space-y-4
                        "
                        >
                          <Select
                            label="Ubicación en almacén *"
                            options={locationOptions}
                            value={formData.location}
                            onChange={(value) =>
                              handleInputChange("location", value)
                            }
                            placeholder="Seleccione ubicación"
                          />

                          <Select
                            label="Proveedor principal *"
                            options={supplierOptions}
                            value={formData.supplier}
                            onChange={(value) =>
                              handleInputChange("supplier", value)
                            }
                            placeholder="Seleccione proveedor"
                          />
                        </div>
                      </div>

                      <div className="space-y-4 sm:space-y-6">
                        <div
                          className="
                          bg-gray-900/90 
                          border border-cyan-900/50 
                          rounded-2xl 
                          p-4 sm:p-5 space-y-4
                        "
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <Input
                              label="Número de lote"
                              value={formData.batch}
                              onChange={(e) =>
                                handleInputChange("batch", e.target.value)
                              }
                              placeholder="LOTE-2024-001"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />

                            <Input
                              label="Número de serie"
                              value={formData.serialNumber}
                              onChange={(e) =>
                                handleInputChange(
                                  "serialNumber",
                                  e.target.value
                                )
                              }
                              placeholder="SN-UNICO-001"
                              className="bg-gray-950/70 border-gray-700 text-slate-50"
                            />
                          </div>

                          <Input
                            label="Fecha de vencimiento"
                            type="date"
                            value={formData.expiryDate}
                            onChange={(e) =>
                              handleInputChange("expiryDate", e.target.value)
                            }
                            className="bg-gray-950/70 border-gray-700 text-slate-50"
                          />

                          <div className="space-y-3 sm:space-y-4">
                            <Checkbox
                              label="📅 Producto perecedero"
                              checked={formData.isPerishable}
                              onChange={(e) =>
                                handleInputChange(
                                  "isPerishable",
                                  e.target.checked
                                )
                              }
                              description="Activa alertas de caducidad automáticas."
                            />

                            <Checkbox
                              label="🔢 Control por lotes"
                              checked={formData.requiresBatchControl}
                              onChange={(e) =>
                                handleInputChange(
                                  "requiresBatchControl",
                                  e.target.checked
                                )
                              }
                              description="Requiere seguimiento individual por lote."
                            />
                          </div>

                          {formData.isPerishable && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4 mt-2">
                              <div className="flex items-center space-x-2 sm:space-x-3">
                                <span className="text-blue-300">📅</span>
                                <div>
                                  <div className="text-sm font-semibold text-blue-200">
                                    Control de caducidad activado
                                  </div>
                                  <div className="text-xs text-blue-200/80 mt-1">
                                    Alertas 30, 15 y 7 días antes del
                                    vencimiento.
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Paso 4: Multimedia y Archivos */}
                {activeStep === 4 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        {/* Archivos */}
                        <div
                          className="
                          bg-gray-900/95 
                          rounded-2xl 
                          p-6 
                          border border-cyan-900/50
                        "
                        >
                          <h4 className="text-lg font-semibold text-slate-50 mb-3 flex items-center gap-2">
                            <span className="text-cyan-300">📎</span>
                            Documentos adjuntos
                          </h4>
                          <p className="text-sm text-slate-400 mb-4">
                            Agrega manuales, especificaciones técnicas,
                            certificados u otros documentos relacionados con el
                            producto.
                          </p>

                          <div className="mb-4 p-4 bg-gray-950/80 rounded-lg border-2 border-dashed border-cyan-900/60 hover:border-cyan-500/70 transition-colors">
                            <FileUpload
                              productId={newProductId}
                              onUpload={handleFileUploaded}
                            />
                          </div>

                          <div className="mt-4">
                            <ProductFiles productId={newProductId} />
                          </div>
                        </div>

                        {/* Imágenes */}
                        <div
                          className="
                          bg-gray-900/95 
                          rounded-2xl 
                          p-6 
                          border border-cyan-900/50
                        "
                        >
                          <h4 className="text-lg font-semibold text-slate-50 mb-4 flex items-center gap-2">
                            <span className="text-cyan-300">🖼️</span>
                            Galería de imágenes
                          </h4>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                            {formData.images.map((image, index) => (
                              <div key={index} className="relative group">
                                <Image
                                  src={image.url}
                                  alt={image.alt}
                                  className="w-full h-20 sm:h-24 rounded-lg sm:rounded-xl object-cover border border-cyan-900/60"
                                />
                                <div className="absolute inset-0 bg-black/65 opacity-0 group-hover:opacity-100 rounded-lg sm:rounded-xl transition-opacity duration-200 flex items-center justify-center space-x-1 sm:space-x-2">
                                  <button
                                    onClick={() => handleRemoveImage(index)}
                                    className="p-1 bg-rose-500 text-white rounded hover:bg-rose-600 transition-colors"
                                  >
                                    <span className="text-xs">🗑️</span>
                                  </button>
                                  <button className="p-1 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition-colors">
                                    <span className="text-xs">🔍</span>
                                  </button>
                                </div>
                                <div className="absolute bottom-1 left-1 right-1">
                                  <div className="text-xs text-white bg-black/60 rounded px-1 truncate">
                                    {image.name}
                                  </div>
                                </div>
                              </div>
                            ))}

                            {formData.images.length < 6 && (
                              <div
                                onClick={() => fileInputRef.current?.click()}
                                className="
                                  border-2 border-dashed border-cyan-900/60 
                                  rounded-lg sm:rounded-xl 
                                  flex flex-col items-center justify-center 
                                  p-4 sm:p-6 cursor-pointer 
                                  hover:border-cyan-500 hover:bg-cyan-500/5 
                                  transition-all duration-200 group
                                "
                              >
                                <span className="text-slate-400 group-hover:text-cyan-300 mb-2 text-xl">
                                  📤
                                </span>
                                <span className="text-sm text-slate-400 group-hover:text-cyan-300 text-center">
                                  Agregar imagen
                                </span>
                                <span className="text-xs text-slate-500 mt-1">
                                  Máx. 6 imágenes
                                </span>
                              </div>
                            )}
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </div>

                        {/* Notas */}
                        <div
                          className="
                          bg-gray-900/95 
                          rounded-2xl 
                          p-4 sm:p-5 
                          border border-cyan-900/50
                        "
                        >
                          <Input
                            label="Notas internas"
                            type="textarea"
                            rows={3}
                            value={formData.notes}
                            onChange={(e) =>
                              handleInputChange("notes", e.target.value)
                            }
                            placeholder="Notas adicionales, observaciones, instrucciones especiales..."
                            className="bg-gray-950/70 border-gray-700 text-slate-50"
                          />
                        </div>
                      </div>

                      {/* Resumen */}
                      <div className="space-y-6">
                        <div
                          className="
                          bg-gray-900/95 
                          rounded-2xl 
                          border border-cyan-900/60 
                          p-6
                        "
                        >
                          <h4 className="text-lg font-semibold text-slate-50 mb-4">
                            Resumen del producto
                          </h4>
                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">SKU:</span>
                              <span className="text-slate-50 font-mono font-semibold">
                                {formData.code || "---"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">Producto:</span>
                              <span className="text-slate-50 font-semibold text-right truncate max-w-[60%]">
                                {formData.name || "---"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">Categoría:</span>
                              <span className="text-cyan-300">
                                {formData.category
                                  ? categoryOptions
                                    .find(
                                      (c) => c.value === formData.category
                                    )
                                    ?.label.split(" ")
                                    .slice(1)
                                    .join(" ")
                                  : "---"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">
                                Stock inicial:
                              </span>
                              <span className="text-slate-50 font-semibold">
                                {formData.currentStock}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">
                                Precio venta:
                              </span>
                              <span className="text-cyan-300 font-semibold">
                                MXN {formData.price || "0.00"}
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-800">
                              <span className="text-slate-400">Archivos:</span>
                              <span className="text-cyan-200 font-semibold">
                                {formData.files.length} adjuntos
                              </span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                              <span className="text-slate-400">Margen:</span>
                              <span className="text-cyan-300 font-semibold">
                                {calculateMargin().toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          className="
                          bg-gray-900/95 
                          rounded-2xl 
                          border border-cyan-900/50 
                          p-4
                        "
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                            <div>
                              <div className="text-sm font-semibold text-slate-50">
                                Listo para guardar
                              </div>
                              <div className="text-xs text-slate-400">
                                Todos los campos requeridos están completos
                                (según el paso actual).
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          className="
          flex flex-col sm:flex-row items-center justify-between 
          px-4 sm:px-8 py-3 sm:py-4 
          border-t border-cyan-900/60 
          bg-gray-900/95 
          space-y-3 sm:space-y-0
        "
        >
          {saveSuccess ? (
            <div className="w-full text-center">
              <Button
                onClick={handleClose}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white"
              >
                Cerrar
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={activeStep === 1 || isSubmitting}
                className="
                  w-full sm:w-auto 
                  px-4 sm:px-6 py-2 sm:py-3 
                  rounded-lg sm:rounded-xl 
                  border-gray-700 
                  text-slate-300 
                  hover:border-gray-500 hover:bg-gray-800 
                  disabled:opacity-50
                "
              >
                Anterior
              </Button>

              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="
                    w-full sm:w-auto 
                    px-4 sm:px-6 py-2 sm:py-3 
                    rounded-lg sm:rounded-xl 
                    text-slate-400 hover:text-slate-100 hover:bg-gray-800 
                    disabled:opacity-50
                  "
                >
                  Cancelar
                </Button>

                {activeStep < steps.length ? (
                  <Button
                    onClick={nextStep}
                    disabled={!isStepValid() || isSubmitting}
                    className="
                      w-full sm:w-auto 
                      px-4 sm:px-6 py-2 sm:py-3 
                      rounded-lg sm:rounded-xl 
                      bg-cyan-500 hover:bg-cyan-600 
                      text-white 
                      disabled:bg-gray-700 disabled:text-slate-500
                    "
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!isStepValid() || isSubmitting}
                    loading={isSubmitting}
                    className="
                      w-full sm:w-auto 
                      px-4 sm:px-6 py-2 sm:py-3 
                      rounded-lg sm:rounded-xl 
                      bg-cyan-500 hover:bg-cyan-600 
                      text-white 
                      disabled:bg-gray-700 disabled:text-slate-500
                    "
                  >
                    {isSubmitting ? "Guardando..." : "Guardar producto"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default React.memo(AddItemModal);
